import type { CanvasArtworkRenderer } from '../render/CanvasArtworkRenderer';
import type { ExportBackground } from '../core/export';

/**
 * Produces a PNG of the current artwork. Delegates composition to the renderer
 * (paper -> paint -> outline, per the export plan) so the exported image always
 * matches what is visible, minus UI and debug overlay.
 */
export class PngExporter {
  constructor(private readonly renderer: CanvasArtworkRenderer) {}

  /** Compose and return a PNG data URL. */
  toDataURL(background: ExportBackground = 'white'): string {
    return this.renderer.composeExport(background).toDataURL('image/png');
  }

  /** Compose and return a PNG Blob (preferred for saving/sharing). */
  toBlob(background: ExportBackground = 'white'): Promise<Blob> {
    const canvas = this.renderer.composeExport(background);
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to encode PNG.'));
      }, 'image/png');
    });
  }

  /**
   * Trigger a browser download of the PNG. On iPad Safari this opens the image
   * in a new context the child's parent can then save to Photos. (Full Photos
   * integration is out of scope for P0A.)
   */
  async download(
    filename = 'my-turtle.png',
    background: ExportBackground = 'white',
  ): Promise<void> {
    const blob = await this.toBlob(background);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke on the next tick so the download has a chance to start.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}
